import { useCallback } from 'react';
import { useLogger, useSelector } from 'core/common/hooks';
import { evalCondition } from 'core/common/utils/evalCondition';
import { EmailFlowAttributes, isEmailFlow } from 'core/flow/entities';
import { getConditionAttributes } from 'core/funnel/store';

export const useEmailFlow = () => {
  const flowConditions = useSelector(getConditionAttributes);
  const logger = useLogger();

  const getEmailFlow = useCallback(
    (emailFlowAttributes: EmailFlowAttributes['emailFlowAttributes']): string => {
      logger.info('EmailFlow: started evaluating emailFlow', {
        configToEvaluate: emailFlowAttributes,
      });

      logger.info('EmailFlow: checking for emailFlow existence');

      if (!emailFlowAttributes || !emailFlowAttributes.emailFlow) {
        logger.info(
          'EmailFlow: emailFlow does not exist or emailFlowAttributes do not exist. Yielding empty string',
          { emailFlow: '' },
        );
        return '';
      }

      logger.info('EmailFlow: emailFlow property exists');

      const emailFlow = emailFlowAttributes.emailFlow.find(isEmailFlow);

      logger.info('EmailFlow: searching for emailFlow.type=EMAIL_FLOW...');

      if (!emailFlow) {
        logger.info('EmailFlow: no EMAIL_FLOW type emailFlow found. Yielding empty string', {
          emailFlow: '',
        });
        return '';
      }

      logger.info('EmailFlow: started evaluating emailFlow attribute names without condition...');

      const emailFlowNamesWithoutCondition = emailFlow.attributes
        .filter((attribute) => !attribute.condition)
        .map((attribute) => attribute.name);

      logger.info('EmailFlow: evaluated emailFlow names without condition', {
        emailFlowNamesWithoutCondition,
      });

      logger.info('EmailFlow: started evaluating emailFlow attribute names with condition...');

      const emailFlowNamesWithCondition = emailFlow.attributes
        .filter((attribute) => !!attribute.condition)
        .filter((attribute) => [flowConditions].find(evalCondition(attribute.condition)))
        .map((attribute) => attribute.name);

      logger.info('EmailFlow: evaluated emailFlow names with condition', {
        emailFlowNamesWithCondition,
      });

      logger.info(
        'EmailFlow: checking whether there is only a single emailFlow name with condition...',
      );

      if (emailFlowNamesWithCondition.length === 1) {
        logger.info('EmailFlow: got only one emailFlow name with condition. Yielding it', {
          emailFlow: emailFlowNamesWithCondition[0],
        });
        return emailFlowNamesWithCondition[0];
      }

      logger.info('EmailFlow: not one emailFlow name with condition');
      logger.info(
        'EmailFlow: checking whether there are multiple emailFlow names with condition...',
      );

      if (emailFlowNamesWithCondition.length > 1) {
        logger.info(
          'EmailFlow: got more than 1 name with condition, cannot evaluate the name. Yielding empty string',
          {
            emailFlow: '',
          },
        );
        return '';
      }

      logger.info(
        'EmailFlow: checking whether there is only a single emailFlow name without condition...',
      );

      if (emailFlowNamesWithoutCondition.length === 1) {
        logger.info('EmailFlow: got only one emailFlow name without condition. Yielding it', {
          emailFlow: emailFlowNamesWithoutCondition[0],
        });
        return emailFlowNamesWithoutCondition[0];
      }

      logger.info('EmailFlow: not one emailFlow name without condition');
      logger.info('EmailFlow: reached default clause. Yielding empty string', {
        emailFlow: '',
      });

      return '';
    },
    // Ignoring store as a dependency, because conditionAttributes are not dynamic
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { getEmailFlow };
};
